/**
 * Global mount point for the knowledge processing console.
 *
 * Rendered by MainLayout, so the run outlives the page that started it: the
 * user can minimise the console, browse to another route, and come back to it
 * from the floating status bubble with the pipeline exactly where they left
 * it. Minimising hides this tree with CSS rather than unmounting it, which is
 * what keeps `usePipelineRun`'s timers - and its `onFinished` handoff to the
 * results panel - running in the background.
 */
import { useNavigate } from "react-router";
import { Upload } from "lucide-react";
import { useProcessingJobStore } from "../../stores/processingJobStore";
import { isValidationPassed } from "./ValidationResultPanel";
import { ProcessingConsole } from "../pages/ContentRegistry/ProcessingConsole";

const CONTENT_REGISTRY_PATH =
  "/continuous-compliance/governance/content-registry";

export function ProcessingJobHost() {
  const navigate = useNavigate();
  const job = useProcessingJobStore((s) => s.job);
  const minimized = useProcessingJobStore((s) => s.minimized);
  const minimize = useProcessingJobStore((s) => s.minimize);
  const cancel = useProcessingJobStore((s) => s.cancel);
  const dismiss = useProcessingJobStore((s) => s.dismiss);
  const pickDifferent = useProcessingJobStore((s) => s.pickDifferent);
  const save = useProcessingJobStore((s) => s.save);
  const markConsoleFinished = useProcessingJobStore(
    (s) => s.markConsoleFinished,
  );
  const setStage = useProcessingJobStore((s) => s.setStage);

  if (!job) return null;

  const passed = job.result ? isValidationPassed(job.result) : false;
  // The result panel and its actions only appear once the console has played
  // its closing stages, the same handoff the wizard used to do itself.
  // Both "uploading" and "running" are in-flight - the action bar stays
  // hidden until the pipeline settles into "complete" or "error".
  const showResults =
    job.consoleFinished &&
    job.phase !== "running" &&
    job.phase !== "uploading";
  // No post-validation upload step anymore (the upload happens up-front),
  // so no "saving" state to disable buttons on.
  const saving = false;

  /* Back to the file picker. The starting flow reopens it when it sees the
     tick; if the user has wandered off, take them back to it first. */
  const goPickDifferent = () => {
    pickDifferent();
    if (!window.location.pathname.startsWith(CONTENT_REGISTRY_PATH)) {
      navigate(CONTENT_REGISTRY_PATH);
    }
  };

  const notice =
    job.phase === "error" && job.error
      ? { title: job.error.title, detail: job.error.message }
      : showResults && job.result && !passed
        ? {
            title: "Document did not pass validation",
            detail:
              "One or more rules failed against this document. Review the failed rules in the feed, or use Back to upload a different file.",
          }
        : null;

  // Back is offered once the run has settled and only when a re-pick is
  // actually useful (nothing to swap out during an error). It replaces the
  // old "Choose Different File" button that used to sit in the footer.
  const canPickDifferent = showResults && job.phase !== "error";

  const footer = (
    <div className="flex w-full flex-wrap items-center justify-end gap-2">
      <button
        type="button"
        onClick={showResults ? dismiss : cancel}
        disabled={saving}
        className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-40"
      >
        Cancel
      </button>
      {showResults && job.phase !== "error" && (
        <button
          type="button"
          onClick={save}
          disabled={saving || !passed}
          title={
            passed
              ? undefined
              : "Validation failed - use Back to upload a different file."
          }
          className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-bold text-white shadow-md shadow-indigo-500/20 transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-indigo-600"
        >
          <Upload className="size-3.5" />
          {saving ? "Saving & Uploading…" : "Save Document"}
        </button>
      )}
    </div>
  );

  return (
    <div className={minimized ? "hidden" : undefined}>
      <ProcessingConsole
        documentTitle={job.documentTitle}
        documentType={job.documentType}
        fileName={job.fileName}
        fileSize={job.fileSize}
        phase={job.phase}
        result={job.result}
        apiDurationMs={job.apiDurationMs}
        onMinimize={minimize}
        onCancel={showResults ? dismiss : cancel}
        onBack={canPickDifferent ? goPickDifferent : undefined}
        onFinished={markConsoleFinished}
        onStageChange={setStage}
        notice={notice}
        footer={footer}
        saving={saving}
        paused={minimized}
      />
    </div>
  );
}
